import * as React from "react";
import * as Frontend from "kmmp/frontend";
import * as Stores from "client/stores";
import { useLocation } from "wouter";
import { useAuth, useState, useWhitelabel } from "client/hooks";
import { Paths } from "shared/paths";
import { uuid } from "shared/uuid";
import PageHeader from "client/components/page-header";
import { compute, Option } from "@nozzlegear/railway";
import { VisualBackgroundSelector } from "client/pages/quick/visual_background_selector";
import { VisualFrameSelector } from "client/pages/quick/visual_frame_selector";
import * as sciPackageConversion from "shared/package-types/sci-portraits";
import * as messengerPackageConversion from "shared/package-types/messenger-portraits";
import * as signaturePackageConversion from "shared/package-types/signature-portraits";
import { purchaseOrderIdIsValid } from "shared/purchase-order-id-is-valid";
import { ImageUploadDialog } from "client/pages/quick/image_upload_dialog";
import { useEngravingFormReducer, usePortraitFormReducer } from  "./reducers";
import { GenericFormControls } from "./GenericFormControls";
import { PortraitFormControls } from "./PortraitFormControls";
import { getBackgrounds, getSignaturePortraitBackgrounds } from "client/image-urls";
import { Unless } from "shared/components";
import { MaybeError } from "shared/components/maybe-error";

type Props = {
    signaturePortraitsOnly?: boolean;
};

type State = {
    error: Option<string>
    customerImage: Option<string>
    showUploadForm: boolean
}

export function QuickPage(props: Props): JSX.Element {
    const whitelabelSettings = useWhitelabel();
    const { companyName, subjectType } = whitelabelSettings;
    const { requireCoupaPurchaseOrder } = useAuth();

    const backgrounds = React.useMemo(() => {
        if (props.signaturePortraitsOnly === true) {
            return getSignaturePortraitBackgrounds();
        } else {
            return getBackgrounds(companyName, subjectType);
        }
    }, [props.signaturePortraitsOnly, companyName, subjectType]);

    const [_, setLocation] = useLocation();
    const [engravingFormState, engravingFormDispatch] = useEngravingFormReducer();
    const [portraitFormState, portraitFormDispatch] = usePortraitFormReducer(backgrounds[0]);
    const [state, setState] = useState<State>({
        error: Option.ofNone(),
        customerImage: Option.ofNone(),
        showUploadForm: false,
    });

    const closeUploadForm = () =>
        setState({ ...state, showUploadForm: false });

    const openUploadForm = () => {

        const error: Option<string> = compute(() => {
            if (Option.isNone(portraitFormState.frameVariant)) {
                return Option.ofSome("You must select a frame.");
            }

            if (Option.isNone(portraitFormState.orientation)) {
                return Option.ofSome("You must select an orientation.");
            }

            if (requireCoupaPurchaseOrder && !portraitFormState.purchaseOrderId) {
                return Option.ofSome(
                    "Your location is part of our HMIS+ integration. Please enter a valid Purchase Order ID from HMIS+."
                );
            } else if (requireCoupaPurchaseOrder && !purchaseOrderIdIsValid(portraitFormState.purchaseOrderId)) {
                return Option.ofSome("You must enter a valid Purchase Order ID.");
            }

            return Option.ofNone();
        });

        setState({
            showUploadForm: error.isNone(),
            error: error,
        });
    };

    const addToCart = async (uploadedImages: Frontend.UploadedImage[]) => {
        if (Option.isNone(portraitFormState.frameVariant)) {
            setState({ error: Option.ofSome("You must select a frame.") });

            return;
        }

        if (Option.isNone(portraitFormState.orientation)) {
            setState({ error: Option.ofSome("You must select an orientation.") });

            return;
        }

        setState({ error: Option.ofNone<string>() });

        if (props.signaturePortraitsOnly !== true) {
            const packageType = compute(() => {
                switch (companyName) {
                    case "sci":
                        const packageWithoutCards = sciPackageConversion.getPackageFromFrameSize(portraitFormState.frameSize);
                        return sciPackageConversion.addCardsToPackage(packageWithoutCards);
                    case "messenger":
                        return messengerPackageConversion.getPackageFromFrameSize(portraitFormState.frameSize);
                    default:
                        const defaultPackageType: Frontend.SCIPackageType = "CP1";

                        return defaultPackageType;
                }
            });

            Stores.Cart.addToCart({
                uuid: uuid(),
                type: "portrait",
                customization: {
                    background: portraitFormState.backgroundVariant,
                    frame: Option.get(portraitFormState.frameVariant),
                    orientation: Option.get(portraitFormState.orientation),
                    package: packageType,
                },
                images: uploadedImages,
                qty: {
                    packages: engravingFormState.quantity,
                    cards: 0,
                    prints: 0,
                },
                source: "quick",
                purchaseOrderId: portraitFormState.purchaseOrderId ?? null,
                deceasedName: "",
                originalOrderId: null,
            });
        } else {
            const packageType = signaturePackageConversion.getPackageFromFrameSize(portraitFormState.frameSize);

            Stores.Cart.addToCart({
                uuid: uuid(),
                type: "signature-portrait",
                customization: {
                    background: portraitFormState.backgroundVariant,
                    frame: Option.get(portraitFormState.frameVariant),
                    orientation: Option.get(portraitFormState.orientation),
                    package: packageType,
                    engravingName: engravingFormState.engravingName,
                    engravingDate1: engravingFormState.engravingDate1,
                    engravingDate2: engravingFormState.engravingDate2,
                    engravingSentiment: engravingFormState.engravingSentiment,
                },
                images: uploadedImages,
                qty: {
                    packages: engravingFormState.quantity,
                },
                source: "quick",
                purchaseOrderId: portraitFormState.purchaseOrderId || null,
                deceasedName: "",
                originalOrderId: null,
            });
        }

        setLocation(whitelabelSettings.siteType === "ecommerce" ? Paths.cart.index : Paths.cart.name);
    };

    const title =
        props.signaturePortraitsOnly === true ? "Add Prestige Portrait to Cart" : "Add Canvas Portrait to Cart";
    const productTitle = props.signaturePortraitsOnly === true ? "Prestige Portrait" : "Canvas Portrait";

    return (
        <section id="cart">
            <PageHeader title={title} />
            <div className="section white pure-g center-children">
                <div className="pure-u-22-24">
                    <div className="pure-u-sm-13-24">
                        <VisualFrameSelector
                            whitelabelSettings={whitelabelSettings}
                            background={portraitFormState.backgroundVariant}
                            frame={portraitFormState.frameVariant}
                            orientation={portraitFormState.orientation}
                            onSelectFrame={f => portraitFormDispatch({type: "setFrame", value: f})}
                            isSignaturePortrait={props.signaturePortraitsOnly === true}
                            displayAsPackage={ companyName === "sci" || companyName === "messenger" }
                        />
                        {/*<OutOfStockWarning frameType="vintage" size="11x14" />*/}
                        <VisualBackgroundSelector
                            whitelabelSettings={whitelabelSettings}
                            background={portraitFormState.backgroundVariant}
                            onSelectBackground={(bg) => portraitFormDispatch({type: "setBackground", value: bg})}
                            allBackgrounds={backgrounds}
                        />
                    </div>
                    <div className="pure-u-sm-visible pure-u-sm-2-24" />
                    <div className="pure-u-1-1 pure-u-sm-9-24">
                        <div className="product main" itemScope={true} itemType="http://schema.org/Product">
                            <h1 className="heading" itemProp="name">
                                {`${portraitFormState.frameSize} ${productTitle}`}
                            </h1>
                            <Unless condition={whitelabelSettings.siteType === "corporate"}>
                                <div>
                                    <hr style={{ margin: "25px 0" }} />
                                    <p>{whitelabelSettings.products[0].description}</p>
                                </div>
                            </Unless>
                            <hr style={{ margin: "25px 0" }} />
                            <div key={"all-controls"}>
                                <PortraitFormControls
                                    requireCoupaPurchaseOrder={requireCoupaPurchaseOrder === true}
                                    signaturePortraitsOnly={props.signaturePortraitsOnly === true}
                                    backgrounds={backgrounds}
                                    state={portraitFormState}
                                    dispatch={portraitFormDispatch}
                                />
                                <GenericFormControls
                                    signaturePortraitsOnly={props.signaturePortraitsOnly === true}
                                    state={engravingFormState}
                                    dispatch={engravingFormDispatch}
                                />
                                <div className="selector-container">
                                    <MaybeError error={state.error} />
                                    <div className="add-to-cart">
                                        <button type="button" className="btn blue" onClick={openUploadForm}>
                                            {"Select Portrait Images"}
                                        </button>
                                    </div>
                                </div>
                                <hr className="marBottom25" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <ImageUploadDialog
                isOpen={state.showUploadForm}
                onClose={closeUploadForm}
                orientation={portraitFormState.orientation.defaultValue("portrait")}
                onImagesUploaded={addToCart}
            />
        </section>
    );
}
